import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Target, 
  Users, 
  Trophy, 
  Star,
  ChevronRight,
  Filter
} from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { getBreadcrumbSchema, getGameSchema } from "@/utils/structuredData";
import { supabase } from "@/integrations/supabase/client";
import { MentorCard } from "@/components/MentorCard";

const MentorValorant = () => {
  const navigate = useNavigate();
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(true);

  // Buscar mentores de Valorant
  useEffect(() => {
    const fetchValorantMentors = async () => {
      try {
        setLoading(true);
        
        // Buscar o jogo Valorant
        const { data: gameData } = await supabase
          .from("games")
          .select("id")
          .ilike("name", "%valorant%")
          .single();

        if (!gameData) {
          console.error("Jogo Valorant não encontrado");
          return;
        }

        // Buscar mentores que oferecem Valorant
        const { data: offerings } = await supabase
          .from("mentorship_offerings")
          .select(`
            mentor_id,
            mentors!inner (
              id,
              name,
              bio,
              image_url,
              slug,
              average_rating,
              total_reviews,
              mentor_info (
                current_rank,
                peak_rank,
                years_experience,
                specialties
              )
            )
          `)
          .eq("game_id", gameData.id)
          .eq("is_active", true);

        if (offerings) {
          // Remover duplicatas e formatar dados
          const uniqueMentors = offerings.reduce((acc, offering) => {
            const mentor = offering.mentors;
            if (!acc.find(m => m.id === mentor.id)) {
              acc.push({
                ...mentor,
                mentor_info: mentor.mentor_info?.[0] || {}
              });
            }
            return acc;
          }, []);

          setMentors(uniqueMentors);
        }
      } catch (error) {
        console.error("Erro ao buscar mentores de Valorant:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchValorantMentors();
  }, []);

  // SEO otimizado
  useSEO({
    title: "Mentores de Valorant - Coaches Profissionais | Arena Pro Player",
    description: `Encontre os melhores mentores de Valorant! ${mentors.length}+ coaches profissionais para te ajudar a subir de rank, melhorar mira e dominar estratégias avançadas.`,
    keywords: "mentor valorant, coach valorant, professor valorant, aulas valorant, treino valorant, subir rank valorant, mira valorant, estratégias valorant",
    canonicalUrl: "https://arena-pro-player.com/mentor-valorant",
    structuredData: [
      getBreadcrumbSchema([
        { name: "Início", url: "https://arena-pro-player.com/" },
        { name: "Mentores", url: "https://arena-pro-player.com/mentores" },
        { name: "Mentores de Valorant", url: "https://arena-pro-player.com/mentor-valorant" }
      ]),
      getGameSchema({ name: "Valorant", cover_url: "/valorant-cover.jpg" }, mentors.length)
    ]
  });

  return (
    <div className="min-h-screen bg-[#0f141c] text-white">
      {/* Hero Section */}
      <section className="relative py-20 px-4 bg-gradient-to-br from-[#0f141c] via-[#1a2332] to-[#0f141c]">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] bg-center opacity-5"></div>
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <Badge className="mb-4 bg-[#FF4655] text-white">
              <Target className="h-4 w-4 mr-2" />
              Valorant
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Mentores de <span className="text-[#FF4655]">Valorant</span>
              <br />
              <span className="text-2xl md:text-3xl text-gray-300">Coaches Profissionais</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Encontre os melhores mentores de Valorant! Coaches experientes para te ajudar 
              a subir de rank, melhorar sua mira e dominar estratégias avançadas.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-[#FF4655] hover:bg-[#E63946] text-white"
                onClick={() => navigate("/como-jogar-valorant")}
              >
                <Target className="h-5 w-5 mr-2" />
                Como Jogar Valorant
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-[#FF4655] text-[#FF4655] hover:bg-[#FF4655] hover:text-white"
                onClick={() => navigate("/mentorias?game=valorant")}
              >
                <Trophy className="h-5 w-5 mr-2" />
                Ver Todas as Mentorias
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Estatísticas */}
      <section className="py-16 px-4 bg-[#181D24]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="text-4xl font-bold text-[#FF4655] mb-2">{mentors.length}+</div>
              <div className="text-gray-300">Mentores Disponíveis</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <div className="text-4xl font-bold text-[#FF4655] mb-2">4.8★</div>
              <div className="text-gray-300">Avaliação Média</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <div className="text-4xl font-bold text-[#FF4655] mb-2">1000+</div>
              <div className="text-gray-300">Alunos Treinados</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Lista de Mentores */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Nossos <span className="text-[#FF4655]">Mentores</span> de Valorant
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto">
              Coaches profissionais com experiência comprovada em Valorant, prontos para 
              acelerar seu progresso no jogo.
            </p>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, index) => (
                <div key={index} className="animate-pulse">
                  <Card className="bg-[#181D24] border-[#222A35]">
                    <CardHeader>
                      <div className="w-16 h-16 bg-gray-700 rounded-full mb-4"></div>
                      <div className="h-6 bg-gray-700 rounded mb-2"></div>
                      <div className="h-4 bg-gray-700 rounded w-3/4"></div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="h-4 bg-gray-700 rounded"></div>
                        <div className="h-4 bg-gray-700 rounded w-5/6"></div>
                        <div className="h-4 bg-gray-700 rounded w-4/6"></div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          ) : mentors.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {mentors.map((mentor, index) => (
                <motion.div
                  key={mentor.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <MentorCard mentor={mentor} />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🎯</div>
              <h3 className="text-2xl font-bold text-white mb-2">
                Mentores em Breve!
              </h3>
              <p className="text-gray-300 mb-6">
                Estamos selecionando os melhores coaches de Valorant para você.
              </p>
              <Button 
                onClick={() => navigate("/como-jogar-valorant")}
                className="bg-[#FF4655] hover:bg-[#E63946] text-white"
              >
                Enquanto isso, aprenda como jogar
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 px-4 bg-gradient-to-r from-[#FF4655] to-[#E63946]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Pronto para Subir de Rank no Valorant?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Escolha seu mentor ideal e comece a treinar hoje mesmo. 
              Acelere seu progresso com coaches profissionais.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-white text-[#FF4655] hover:bg-gray-100"
                onClick={() => navigate("/mentores")}
              >
                <Users className="h-5 w-5 mr-2" />
                Ver Todos os Mentores
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white hover:text-[#FF4655]"
                onClick={() => navigate("/como-jogar-valorant")}
              >
                <Target className="h-5 w-5 mr-2" />
                Guia Completo de Valorant
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default MentorValorant; 